import { Location } from '../services/locationAPI';

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec'] as const;

type Month = typeof MONTHS[number];

/** The monthly seasonal fields the daily engine reads, added by withLocationRates. */
export type MonthlyRates = Partial<Record<`adr_${Month}` | `occupancy_${Month}`, number>>;

/**
 * Copy a property's location's monthly ADR/occupancy onto the property before it
 * is sent to the backend engine.
 *
 * The daily cashflow engine reads `adr_<month>` / `occupancy_<month>` and falls
 * back to a flat €155 / 30% when they are absent. Properties are stored with
 * only a `location_id` (the seasonal figures live on the Location record in D1,
 * which the FastAPI engine cannot see), so without this the configured seasonal
 * ADR/occupancy never reaches the engine and every projection runs on the flat
 * fallback. Returns the property unchanged when it has no matching location.
 */
export function withLocationRates<T extends Record<string, any>>(
  property: T,
  locations: Location[],
): T & MonthlyRates {
  const loc = locations.find((l) => l.id === property.location_id);
  if (!loc) return property as T & MonthlyRates;
  const monthly: MonthlyRates = {};
  for (const m of MONTHS) {
    monthly[`adr_${m}`] = (loc as any)[`adr_${m}`];
    monthly[`occupancy_${m}`] = (loc as any)[`occupancy_${m}`];
  }
  return { ...property, ...monthly };
}
